import { memo, useCallback, useMemo, useState } from "react";

import { cn } from "@/lib/chillnn/cn";

import { InputNumber } from "@/components/ui/input/input-number";
import { Slider } from "@/components/ui/slider/slider";

type Props = {
	calcAdultPriceAsTotalNum: boolean;
};

// 初期データ (6名まで)
const DEFAULT_PRICES = [10000, 20000, 27000, 34000, 41000, 47000];

export const KidsPolicyCalculationDemo = memo(({ calcAdultPriceAsTotalNum }: Props) => {
	// 人数ごとの料金設定
	const [prices, setPrices] = useState<number[]>([...DEFAULT_PRICES]);

	// 計算デモの入力状態
	const [adults, setAdults] = useState(2);
	const [children, setChildren] = useState(1);
	const [policy, setPolicy] = useState(50);

	// 価格取得ヘルパー
	const getPrice = useCallback(
		(count: number) => {
			if (count <= 0) return 0;
			// 6名を超える場合は、最大の6名料金を返す
			if (count > prices.length) return prices[prices.length - 1];
			return prices[count - 1];
		},
		[prices],
	);

	// 価格更新処理
	const updatePrice = useCallback((index: number, value: number) => {
		setPrices((prev) => {
			const newPrices = [...prev];
			newPrices[index] = value;
			return newPrices;
		});
	}, []);

	// 計算ロジック（両方のモードで計算）
	const { adultBasedResult, totalBasedResult } = useMemo(() => {
		const totalCount = adults + children;

		if (adults === 0) {
			const emptyResult = { totalNormal: 0, totalAllAdult: 0, final: 0, winnerIsNormal: true };
			return { adultBasedResult: emptyResult, totalBasedResult: emptyResult };
		}

		// 大人の人数基準
		const adultBasePrice = getPrice(adults);
		const adultUnitPrice = adultBasePrice / adults;
		const adultChildPrice = adultUnitPrice * children * (policy / 100);
		const adultTotalNormal = Math.ceil(adultBasePrice + adultChildPrice);
		const adultTotalAllAdult = getPrice(totalCount);
		const adultFinal = Math.min(adultTotalNormal, adultTotalAllAdult);
		const adultWinnerIsNormal = adultTotalNormal <= adultTotalAllAdult;

		// 合計人数基準
		const totalBasePriceTotal = getPrice(totalCount);
		const totalUnitPrice = totalBasePriceTotal / totalCount;
		const totalAdultPart = totalUnitPrice * adults;
		const totalChildPart = totalUnitPrice * children * (policy / 100);
		const totalTotalNormal = Math.ceil(totalAdultPart + totalChildPart);
		const totalTotalAllAdult = getPrice(totalCount);
		const totalFinal = Math.min(totalTotalNormal, totalTotalAllAdult);
		const totalWinnerIsNormal = totalTotalNormal <= totalTotalAllAdult;

		return {
			adultBasedResult: {
				totalNormal: adultTotalNormal,
				totalAllAdult: adultTotalAllAdult,
				final: adultFinal,
				winnerIsNormal: adultWinnerIsNormal,
			},
			totalBasedResult: {
				totalNormal: totalTotalNormal,
				totalAllAdult: totalTotalAllAdult,
				final: totalFinal,
				winnerIsNormal: totalWinnerIsNormal,
			},
		};
	}, [adults, children, policy, getPrice]);

	return (
		<div className="space-y-4">
			<div>
				<h5 className="text-[17px] font-semibold leading-[24px]">キッズポリシーの計算デモ</h5>
				<p className="mt-1 text-xs text-caption-50">
					※「キッズポリシーが適用された料金」が、「全員が大人の場合の料金」より高くなる場合は、「全員が大人の場合の料金」が実際の料金として採用されます。
				</p>
			</div>
			{/* 入力セクション */}
			<div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
				<div className="border-b border-zinc-200 bg-zinc-50 px-4 py-2">
					<h3 className="text-sm font-bold">デモ用の設定</h3>
				</div>

				<div className="flex flex-col gap-y-10 p-4">
					{/* 人数ごとの部屋料金設定 */}
					<div className="flex flex-col">
						<div className="mb-4 flex flex-col gap-y-1.5">
							<span className="text-sm font-bold">人数ごとの部屋料金設定</span>
							<span className="text-xs text-caption-50">※7名以上は6名の料金を適用</span>
						</div>
						{/* 人数ごとの料金 */}
						<div className="grid grid-cols-3 gap-x-1.5 gap-y-2 pl-3">
							{prices.map((price, index) => (
								<div key={index} className="">
									<div className="mb-1 text-xs">{index + 1}名</div>
									<div className="flex items-end gap-x-1">
										<InputNumber
											className="w-full text-center text-xs"
											value={price}
											onChange={(e) => updatePrice(index, Number(e.target.value))}
										/>
										<span className="text-xs">円</span>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* 大人・子供 */}
					<div>
						<span className="mb-1.5 block text-sm font-bold">大人・子供の人数</span>
						<div className="flex items-end gap-x-12 pl-3">
							<div>
								<div className="mb-1 text-xs ">大人</div>
								<div className="flex items-end gap-x-1">
									<InputNumber
										className="w-14 text-center"
										value={adults}
										min={1}
										max={10}
										onChange={(e) => setAdults(Number(e.target.value))}
									/>
									<span className="text-sm">人</span>
								</div>
							</div>
							<div>
								<div className="mb-1 text-xs ">子供</div>
								<div className="flex items-end gap-x-1">
									<InputNumber
										className="w-14 text-center"
										value={children}
										min={0}
										max={10}
										onChange={(e) => setChildren(Number(e.target.value))}
									/>
									<span className="text-sm">人</span>
								</div>
							</div>
						</div>
					</div>

					{/* キッズポリシー */}
					<div>
						<div className="mb-1.5 text-sm font-bold">キッズポリシー（子供の料金）</div>
						<div className="flex items-center gap-x-2 pl-3">
							<Slider
								min={0}
								max={100}
								step={10}
								value={[policy]}
								onValueChange={(value: number[]) => setPolicy(value[0])}
							/>
							<span className="text-lg font-bold text-brand">{policy}%</span>
						</div>
					</div>
				</div>
			</div>

			{/* 計算デモセクション - 両方表示 */}
			<div className="grid grid-cols-2 gap-4">
				{/* 大人の人数の料金を基準 */}
				<div
					className={cn(
						"overflow-hidden rounded-lg border bg-white transition-opacity",
						!calcAdultPriceAsTotalNum ? "border-brand" : "border-zinc-200 opacity-50",
					)}
				>
					<div
						className={cn(
							"border-b px-4 py-3",
							!calcAdultPriceAsTotalNum ? "border-brand/20 bg-brand/5" : "border-zinc-200 bg-zinc-50",
						)}
					>
						<h3
							className={cn(
								"text-sm font-bold",
								!calcAdultPriceAsTotalNum ? "text-brand" : "text-zinc-400",
							)}
						>
							大人の人数の料金を基準
						</h3>
						<p className="mt-1 text-xs text-caption-50">「大人の人数」で決まる単価をベースに計算します。</p>
					</div>
					<div className="space-y-3 p-4">
						{/* 採用される料金 */}
						<div
							className={cn(
								"rounded-xl border-2 p-3 text-center",
								!calcAdultPriceAsTotalNum ? "border-brand bg-brand/5" : "border-zinc-200 bg-zinc-50",
							)}
						>
							<div
								className={cn(
									"mb-1 text-xs font-bold",
									!calcAdultPriceAsTotalNum ? "text-brand" : "text-zinc-400",
								)}
							>
								採用される料金 (税込)
							</div>
							<div
								className={cn(
									"text-2xl font-bold",
									!calcAdultPriceAsTotalNum ? "text-brand" : "text-zinc-400",
								)}
							>
								¥{adultBasedResult.final.toLocaleString()}
							</div>
						</div>

						{/* 計算結果 */}
						<div className="space-y-2">
							<div
								className={cn(
									"rounded-lg border p-2 text-center",
									adultBasedResult.winnerIsNormal && !calcAdultPriceAsTotalNum
										? "border-blue-200 bg-blue-50"
										: "border-zinc-200 bg-zinc-50",
								)}
							>
								<div className="text-[10px] text-caption-50">キッズポリシーが適用された料金</div>
								<div
									className={cn(
										"text-sm font-bold",
										adultBasedResult.winnerIsNormal && !calcAdultPriceAsTotalNum
											? "text-blue-600"
											: "text-zinc-400",
									)}
								>
									¥{adultBasedResult.totalNormal.toLocaleString()}
								</div>
							</div>
							<div
								className={cn(
									"rounded-lg border p-2 text-center",
									!adultBasedResult.winnerIsNormal && !calcAdultPriceAsTotalNum
										? "border-blue-200 bg-blue-50"
										: "border-zinc-200 bg-zinc-50",
								)}
							>
								<div className="text-[10px] text-caption-50">全員が大人の場合の料金</div>
								<div
									className={cn(
										"text-sm font-bold",
										!adultBasedResult.winnerIsNormal && !calcAdultPriceAsTotalNum
											? "text-blue-600"
											: "text-zinc-400",
									)}
								>
									¥{adultBasedResult.totalAllAdult.toLocaleString()}
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* 合計人数の料金を基準 */}
				<div
					className={cn(
						"overflow-hidden rounded-lg border bg-white transition-opacity",
						calcAdultPriceAsTotalNum ? "border-brand" : "border-zinc-200 opacity-50",
					)}
				>
					<div
						className={cn(
							"border-b px-4 py-3",
							calcAdultPriceAsTotalNum ? "border-brand/20 bg-brand/5" : "border-zinc-200 bg-zinc-50",
						)}
					>
						<h3
							className={cn(
								"text-sm font-bold",
								calcAdultPriceAsTotalNum ? "text-brand" : "text-zinc-400",
							)}
						>
							合計人数の料金を基準
						</h3>
						<p className="mt-1 text-xs text-caption-50">
							「大人＋子供の人数」で決まる単価をベースに計算します。
						</p>
					</div>
					<div className="space-y-3 p-4">
						{/* 採用される料金 */}
						<div
							className={cn(
								"rounded-xl border-2 p-3 text-center",
								calcAdultPriceAsTotalNum ? "border-brand bg-brand/5" : "border-zinc-200 bg-zinc-50",
							)}
						>
							<div
								className={cn(
									"mb-1 text-xs font-bold",
									calcAdultPriceAsTotalNum ? "text-brand" : "text-zinc-400",
								)}
							>
								採用される料金 (税込)
							</div>
							<div
								className={cn(
									"text-2xl font-bold",
									calcAdultPriceAsTotalNum ? "text-brand" : "text-zinc-400",
								)}
							>
								¥{totalBasedResult.final.toLocaleString()}
							</div>
						</div>

						{/* 計算結果 */}
						<div className="space-y-2">
							<div
								className={cn(
									"rounded-lg border p-2 text-center",
									totalBasedResult.winnerIsNormal && calcAdultPriceAsTotalNum
										? "border-blue-200 bg-blue-50"
										: "border-zinc-200 bg-zinc-50",
								)}
							>
								<div className="text-[10px] text-caption-50">キッズポリシーが適用された料金</div>
								<div
									className={cn(
										"text-sm font-bold",
										totalBasedResult.winnerIsNormal && calcAdultPriceAsTotalNum
											? "text-blue-600"
											: "text-zinc-400",
									)}
								>
									¥{totalBasedResult.totalNormal.toLocaleString()}
								</div>
							</div>
							<div
								className={cn(
									"rounded-lg border p-2 text-center",
									!totalBasedResult.winnerIsNormal && calcAdultPriceAsTotalNum
										? "border-blue-200 bg-blue-50"
										: "border-zinc-200 bg-zinc-50",
								)}
							>
								<div className="text-[10px] text-caption-50">全員が大人の場合の料金</div>
								<div
									className={cn(
										"text-sm font-bold",
										!totalBasedResult.winnerIsNormal && calcAdultPriceAsTotalNum
											? "text-blue-600"
											: "text-zinc-400",
									)}
								>
									¥{totalBasedResult.totalAllAdult.toLocaleString()}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

KidsPolicyCalculationDemo.displayName = "KidsPolicyCalculationDemo";
